import { z } from "zod";

// The roleplay game's wire types (play contract 4, player view 3; spark#1075). Every response, snapshot and run result
// is parsed here before anything reads it: validated, never cast. zod drops every field this build does not know, so
// whatever a server might add (or leak) is neither kept nor shown, and a field typed `T | null` that is missing reads
// as null (§3.1). A value the contract lets spark fall back on (an unknown fact kind, item category, item wear,
// player condition, status or goal state) is caught to a fallback instead of failing the whole payload (§7).

// Contract 4 (agent-service#129, spark#1075): the run-ending skip route went and the restart route came, both changes of
// the routes, and the tutorial's goals changed meaning. View 3 retypes `SituationView.goals`: a tree of the story's
// main goals with the scene's goals beneath them, every goal with its state (open, reached or crossed out) in place of
// view 2's flat list with `reached`. Both change what the wire means, so spark and agent-service switch together: a
// response of another contract or view reads as "update needed" (§7). Contract 3 (96, 97) made a turn need text
// (`text_required`) and let every bar list all of its statements with whether they are answered (`statements`). The
// tutorial's summary (126) and `SituationView.options`, the clickable courses of action (spark#1075), stay optional
// fields (§7).
export const ROLEPLAY_CONTRACT = 5;
export const ROLEPLAY_VIEW_VERSION = 3;

export const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

// §5.1, as the browser sees them (§5.2), plus the BFF's own 501.
export const ROLEPLAY_ERROR_CODES = [
  "ROLEPLAY_INVALID_INPUT",
  "ROLEPLAY_TEXT_REQUIRED",
  "ROLEPLAY_UNSUPPORTED_CONTRACT",
  "ROLEPLAY_GAME_NOT_FOUND",
  "ROLEPLAY_TURN_IN_PROGRESS",
  "ROLEPLAY_STALE_TURN",
  "ROLEPLAY_NARRATION_OWED",
  "ROLEPLAY_SCENE_ENDED",
  "ROLEPLAY_NO_NARRATION_OWED",
  "ROLEPLAY_POWERS_NOT_LOCKED",
  "ROLEPLAY_GAME_CLOSED",
  "ROLEPLAY_GAME_SUSPENDED",
  "ROLEPLAY_NOT_PLAYABLE",
  "ROLEPLAY_GAME_EXPIRED",
  "ROLEPLAY_GENESIS_UNAVAILABLE",
  "ROLEPLAY_UNAVAILABLE",
  "ROLEPLAY_NARRATOR_FAILED",
  "ROLEPLAY_INTERNAL",
  "ROLEPLAY_NOT_SUPPORTED",
] as const;

// UPDATE_NEEDED is spark's own: a payload of a contract or view version this build does not speak, or one it cannot
// read (§7). UNKNOWN is everything outside the vocabulary: an AGENT_ERROR, a dropped connection, a code added later.
export type RoleplayErrorCode = (typeof ROLEPLAY_ERROR_CODES)[number] | "ROLEPLAY_UPDATE_NEEDED" | "UNKNOWN";

const KNOWN_CODES: ReadonlySet<string> = new Set(ROLEPLAY_ERROR_CODES);

// One vocabulary for both paths (§5.2): the BFF's `ROLEPLAY_TURN_IN_PROGRESS` and the stream's in-band
// `turn_in_progress` meet here.
export function normalizeRoleplayCode(raw: unknown): RoleplayErrorCode {
  if (typeof raw !== "string" || raw === "") return "UNKNOWN";
  const code = raw.startsWith("ROLEPLAY_") ? raw : `ROLEPLAY_${raw.toUpperCase()}`;
  return KNOWN_CODES.has(code) ? (code as RoleplayErrorCode) : "UNKNOWN";
}

export class RoleplayError extends Error {
  constructor(
    readonly code: RoleplayErrorCode,
    readonly status?: number,
  ) {
    super(code);
    this.name = "RoleplayError";
  }
}

export function roleplayErrorCode(error: unknown): RoleplayErrorCode {
  return error instanceof RoleplayError ? error.code : "UNKNOWN";
}

const int = z.number().int();
const count = int.min(0);
const nullableInt = int.nullish().transform((value) => value ?? null);
const uuid = z.string().regex(UUID_RE);
// A per-game token for a character or an item (§3.5): opaque, used only as a key.
const token = z.string().min(1).max(128);
const localizedText = z.object({ de: z.string(), en: z.string() });
export type LocalizedText = z.output<typeof localizedText>;

const language = z.enum(["de", "en"]).catch("de");
export type StoryLanguage = z.output<typeof language>;

export function localized(text: LocalizedText, lang: StoryLanguage): string {
  return text[lang];
}

export const GAME_STATUSES = ["open", "finished", "abandoned", "expired"] as const;
// `unknown` stands for a status added after this build: the page then shows the game read only.
const gameStatus = z.enum([...GAME_STATUSES, "unknown"]).catch("unknown");
export type GameStatus = z.output<typeof gameStatus>;

// What the story has to tell before anything else (§3.3). A kind added later still means "something is owed".
const narrationOwed = z
  .string()
  .nullish()
  .transform((value): "opening" | "closing" | null => (value == null ? null : value === "closing" ? "closing" : "opening"));
export type NarrationOwed = z.output<typeof narrationOwed>;

// Every statement one power's bar counts, each with whether the run's answers have answered it (97, amending 80: all
// statements are listed in the bar's dropdown): in the tutorial and in a scored game alike the
// power's fixed questionnaire statements, all of them, as many as the bar's total (103), and each one can be answered,
// since the assessor reads every answer against all statements of the five powers (101 in the tutorial, those of the
// cluster it focuses on; 104 in a scored game, whose ticks come once genesis stores readings). Their texts and that
// flag only, never an id, a value or a confidence (zod drops whatever else an item carries). Tolerant like every
// optional field (§7): a missing or unreadable list reads as empty, an item that does not read is dropped (no text in
// either language, or an `answered` that is not true or false), and a text missing or blank in one language falls
// back to the other. The order is the server's; the page shows the unanswered ones first (bars.ts).
const statementItem = z.object({
  text: z.object({ de: z.string().nullish(), en: z.string().nullish() }),
  answered: z.boolean(),
});

export interface BarStatement {
  text: LocalizedText;
  answered: boolean;
}

function shownText(value: string | null | undefined): string | null {
  return value != null && value.trim() !== "" ? value : null;
}

const statements = z
  .array(z.unknown())
  .nullish()
  .catch(null)
  .transform((items) =>
    (items ?? []).flatMap((item): BarStatement[] => {
      const parsed = statementItem.safeParse(item);
      if (!parsed.success) return [];
      const { text, answered } = parsed.data;
      const shownDe = shownText(text.de) ?? shownText(text.en);
      const shownEn = shownText(text.en) ?? shownText(text.de);
      return shownDe !== null && shownEn !== null ? [{ text: { de: shownDe, en: shownEn }, answered }] : [];
    }),
  );

const bars = z.object({
  thresholdPercent: int.min(0).max(100),
  state: z.enum(["current", "pending"]).catch("pending"),
  powers: z.array(z.object({ power: z.string().min(1), covered: count, total: count, statements })),
});
export type Bars = z.output<typeof bars>;

// What a completed tutorial ends with (spark#1075, Entscheidung 126): a short summary of the storyline and of the
// decisions the player made, then the score of each power. `recap` is the narrator's short recap of the story
// and of what the player decided, plain text in the story's language like a narration, or null where none was written.
// `scores` holds one value per power of the focus: a whole number from 0 to 100, worked out as genesis works out a
// scored game's five values but without the 60 % rule, or null for a power no answer has read on yet. They count
// nowhere (62). agent-service sends the summary on a completed tutorial only. Tolerant like every optional field (§7):
// a missing or unreadable summary reads as null, a recap that is not text or is blank as null, and a score that does
// not read is dropped. The order is the server's; the page shows spark's (bars.ts).
const powerScore = z.object({
  power: z.string().min(1),
  score: int.min(0).max(100).nullish().transform((value) => value ?? null),
  counted: z.boolean(),
});

export interface PowerScore {
  power: string;
  score: number | null;
  counted: boolean;
}

const endSummary = z
  .object({
    recap: z.string().nullish().catch(null).transform((value) => shownText(value)),
    scores: z
      .array(z.unknown())
      .nullish()
      .catch(null)
      .transform((items) =>
        (items ?? []).flatMap((item): PowerScore[] => {
          const parsed = powerScore.safeParse(item);
          return parsed.success ? [parsed.data] : [];
        }),
      ),
  })
  .nullish()
  .catch(null)
  .transform((value) => value ?? null);
export type GameEndSummary = NonNullable<z.output<typeof endSummary>>;

const gameSummary = z.object({
  id: uuid,
  gameKey: z.string().min(1),
  language,
  status: gameStatus,
  turn: count,
  scene: count,
  sceneEnded: z.boolean(),
  storyEnded: z.boolean(),
  narrationOwed,
  turnInProgress: z.boolean().optional().transform((value) => value ?? false),
  // Whether the run that holds the game's lock still beats (§4.5, §5.3, agent-service#129): an older agent-service
  // sends no such field, and then only failed reads end spark's wait, so a missing one counts as alive.
  runAlive: z.boolean().optional().transform((value) => value ?? true),
  bars,
  startedAt: z.string(),
  lastActivityAt: z.string(),
  expiresAt: z.string().nullish().transform((value) => value ?? null),
  summary: endSummary,
});
export type GameSummary = z.output<typeof gameSummary>;

export const FACT_KINDS = ["name", "age", "profession", "hobby", "trait", "relationship", "mood"] as const;
const factKind = z.enum([...FACT_KINDS, "unknown"]).catch("unknown");
export type FactKind = z.output<typeof factKind>;

export const ITEM_CATEGORIES = ["food", "drink", "device", "tool", "container", "other"] as const;

const itemLeaf = z.object({
  id: token,
  name: localizedText,
  description: localizedText,
  category: z.enum(ITEM_CATEGORIES).catch("other"),
  usesLeft: nullableInt,
  charge: nullableInt,
  capacity: nullableInt,
  freshness: z
    .enum(["normal", "questionable", "spoiled"])
    .nullish()
    .catch(null)
    .transform((value) => value ?? null),
  // Wear (78): the stage of an item that wears out with use and over time. An item that does not wear, a field an
  // older agent-service does not send yet and a stage added later all read as null, which shows nothing (§3.5, §7).
  condition: z
    .enum(["good", "worn", "broken"])
    .nullish()
    .catch(null)
    .transform((value) => value ?? null),
});
// A container's items, one level (33): what a contained item contains is not read.
const itemView = itemLeaf.extend({
  contents: z
    .array(itemLeaf)
    .optional()
    .transform((value) => value ?? []),
});
export type ItemView = z.output<typeof itemView>;
export type ContainedItem = z.output<typeof itemLeaf>;

const characterView = z.object({
  id: token,
  placeholder: localizedText,
  presence: z.enum(["met", "heard_of"]).catch("heard_of"),
  available: z.boolean(),
  shownMood: localizedText.nullish().transform((value) => value ?? null),
  facts: z.array(z.object({ kind: factKind, value: localizedText })),
  items: z.object({
    known: z.array(z.object({ id: token, name: localizedText })),
    complete: z.boolean(),
  }),
});
export type CharacterView = z.output<typeof characterView>;

const POLE_ID_RE = /^p[1-9][0-9]{0,2}$/;

const OPTION_ID_RE = /^a[1-9][0-9]{0,2}$/;
const FOLLOW_UP_ID_RE = /^a[1-9][0-9]{0,2}\.[1-9][0-9]{0,2}$/;

// A course of action's follow-up (§3.5; agent-service#129, spark#1075): a click puts its template, the start of a move
// without the trailing space, at the start of the field. `ownWords` marks „Mit eigenen Worten …“, always the last.
const followUpView = z.object({
  id: z.string().regex(FOLLOW_UP_ID_RE),
  label: localizedText,
  template: localizedText,
  ownWords: z.boolean(),
});
const optionView = z.object({ id: z.string().regex(OPTION_ID_RE), label: localizedText, followUps: z.array(followUpView).min(1) });
export type OptionView = z.output<typeof optionView>;
export type FollowUpView = z.output<typeof followUpView>;

// A goal as the tutorial shows it (§3.5, view 3; agent-service#129): the story's main goals at the top, each with its
// subgoals, the current scene's goals beneath the main goal they serve. Three levels at most, read as three explicit
// schemas so that nothing deeper than the third level is ever kept: a third-level goal's own subgoals are dropped.
// `state` is the server's, never worked out here: `crossedOut` means closed for good. `scope` says whether a goal is
// the story's (a main goal or one of its own subgoals, the same in every scene) or the current scene's. Tolerant as
// every field that may grow (§7): a state or scope added later reads as `open` or `scene`, missing `subgoals` read as
// none, and a goal of view 2's flat shape, which said only whether it was `reached`, reads as such a scene goal. A
// list that does not read at all reads as no goals, so a broken list hides the goals rather than failing the game.
const GOAL_STATES = ["open", "reached", "crossedOut"] as const;
export type GoalState = (typeof GOAL_STATES)[number];
export type GoalScope = "story" | "scene";

export interface GoalView {
  id: string;
  text: LocalizedText;
  state: GoalState;
  scope: GoalScope;
  subgoals: GoalView[];
}

function goalAt<S extends z.ZodTypeAny>(subgoals: S) {
  return z.preprocess(
    // View 2's goal said `reached: true` where view 3 says `state: "reached"`.
    (value) =>
      value !== null && typeof value === "object" && (value as { state?: unknown }).state === undefined
        ? { ...value, state: (value as { reached?: unknown }).reached === true ? "reached" : "open" }
        : value,
    z.object({
      id: z.string().min(1).max(32),
      text: localizedText,
      state: z.enum(GOAL_STATES).catch("open"),
      scope: z.enum(["story", "scene"]).catch("scene"),
      subgoals,
    }),
  );
}

const noSubgoals = z.unknown().transform((): GoalView[] => []);
const goal3 = goalAt(noSubgoals);
const goal2 = goalAt(z.array(goal3).nullish().transform((value) => value ?? []));
const goal1 = goalAt(z.array(goal2).nullish().transform((value) => value ?? []));

const goals = z
  .array(goal1)
  .nullish()
  .catch(null)
  .transform((value): GoalView[] => (value ?? []) as GoalView[]);

const situationView = z.object({
  poles: z.array(z.object({ id: z.string().regex(POLE_ID_RE), text: localizedText })),
  // The situation's clickable courses of action (§3.5): an optional field of view 2 (§7). Missing (an older
  // agent-service) or not readable, it reads as none, so a broken list hides the options rather than failing the game.
  // It holds only the options whose conditions hold now, so it may change after any turn within a scene
  // (agent-service#129); spark renders whatever list arrives.
  options: z
    .array(optionView)
    .nullish()
    .catch(null)
    .transform((value) => value ?? []),
  goals,
  ended: z.boolean(),
});
export type SituationView = z.output<typeof situationView>;

const playerView = z.object({
  dateTime: z.string(),
  situation: situationView.nullish().transform((value) => value ?? null),
  inventory: z.array(itemView),
  cast: z.array(characterView),
  conditions: z
    .array(z.enum(["food_poisoning", "unknown"]).catch("unknown"))
    .optional()
    .transform((value) => value ?? []),
});
export type PlayerView = z.output<typeof playerView>;

const turnEntry = z.object({
  type: z.literal("turn"),
  turn: count,
  scene: count,
  runId: uuid,
  at: z.string(),
  input: z.object({
    text: z.string(),
    pole: z
      .object({ id: z.string(), text: localizedText })
      .nullish()
      .transform((value) => value ?? null),
  }),
  narration: z.string(),
});
const openingEntry = z.object({ type: z.literal("opening"), scene: count, runId: uuid, at: z.string(), text: z.string() });
const closingEntry = z.object({ type: z.literal("closing"), scene: count, runId: uuid, at: z.string(), text: z.string() });
const sceneEndEntry = z.object({ type: z.literal("scene_end"), scene: count });
const logEntry = z.discriminatedUnion("type", [turnEntry, openingEntry, closingEntry, sceneEndEntry]);
export type LogEntry = z.output<typeof logEntry>;
export type TurnLogEntry = z.output<typeof turnEntry>;

const KNOWN_LOG_TYPES: ReadonlySet<string> = new Set(["turn", "opening", "closing", "scene_end"]);
// An entry of a type added later is skipped; an entry of a known type that does not read fails the whole log, so a
// stored turn can never silently vanish from what the page checks after a stall (§4.5).
const laterLogEntry = z
  .object({ type: z.string() })
  .refine((entry) => !KNOWN_LOG_TYPES.has(entry.type))
  .transform(() => null);

const log = z.object({
  entries: z
    .array(z.union([logEntry, laterLogEntry]))
    .transform((entries) => entries.filter((entry): entry is LogEntry => entry !== null)),
  truncated: z.boolean(),
});
export type Log = z.output<typeof log>;

const limits = z.object({ maxTextLength: int.positive() });
export type Limits = z.output<typeof limits>;

const versions = { contract: z.literal(ROLEPLAY_CONTRACT), viewVersion: z.literal(ROLEPLAY_VIEW_VERSION) };

const gameResponse = z.object({ ...versions, limits, game: gameSummary, view: playerView, log });
export type GameResponse = z.output<typeof gameResponse>;

const snapshot = z.object({ ...versions, game: gameSummary, view: playerView });
export type Snapshot = z.output<typeof snapshot>;

const unavailable = z.string().nullish().transform((value) => value ?? null);

const catalogue = z.object({
  ...versions,
  limits,
  games: z.array(
    z.object({
      gameKey: z.string().min(1),
      available: z.boolean(),
      unavailable,
      story: gameSummary.nullish().transform((value) => value ?? null),
    }),
  ),
});
export type Catalogue = z.output<typeof catalogue>;
export type CatalogueGame = Catalogue["games"][number];

const runResult = z.object({ turn: count, status: gameStatus, sceneEnded: z.boolean(), narrationOwed });
export type RunResult = z.output<typeof runResult>;

// Why the narrator gave up (spark#1075, Entscheidung 125), coarsely, sent with `narrator_failed` only (§5.1): its last
// attempt, after every rewrite (81), still told something the engine refused (`refused`), still showed what is hidden
// (`leak`) or gave no answer that could be read (`model`). Never what was refused or why, so nothing hidden comes
// through. A reason added later, one that does not read and a missing one (an older agent-service) read as `model`.
export const NARRATOR_FAILURES = ["refused", "leak", "model"] as const;
const narratorFailure = z.enum(NARRATOR_FAILURES).catch("model");
export type NarratorFailure = z.output<typeof narratorFailure>;

/** A RUN_ERROR as spark reads it: its code (§5.2) and, with `narrator_failed` only, why the narrator gave up. */
export interface RunError {
  code: RoleplayErrorCode;
  reason?: NarratorFailure;
}

function parseOrUpdateNeeded<T>(schema: z.ZodType<T>, value: unknown): T {
  const parsed = schema.safeParse(value);
  if (!parsed.success) throw new RoleplayError("ROLEPLAY_UPDATE_NEEDED");
  return parsed.data;
}

export function parseGameResponse(value: unknown): GameResponse {
  return parseOrUpdateNeeded(gameResponse, value);
}

export function parseCatalogue(value: unknown): Catalogue {
  return parseOrUpdateNeeded(catalogue, value);
}

export function parseSnapshot(value: unknown): Snapshot | null {
  const parsed = snapshot.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function parseRunResult(value: unknown): RunResult | null {
  const parsed = runResult.safeParse(value);
  return parsed.success ? parsed.data : null;
}

// Every RUN_ERROR reads, as before 125: its code as `normalizeRoleplayCode` reads it, and a reason only beside
// `narrator_failed`, whatever another code sends with it.
export function parseRunError(event: { code?: unknown; reason?: unknown; metadata?: unknown }): RunError {
  const code = normalizeRoleplayCode(event.code);
  if (code !== "ROLEPLAY_NARRATOR_FAILED") return { code };
  // From version 1.0 the AG-UI client removes a top-level field it does not know, `reason` included, and keeps
  // `metadata`; agent-service sends the reason in both, so either client reads it (spark#1075, Entscheidung 125).
  const metadata = event.metadata;
  const fromMetadata = typeof metadata === "object" && metadata !== null ? (metadata as { reason?: unknown }).reason : undefined;
  return { code, reason: narratorFailure.parse(fromMetadata ?? event.reason) };
}
